import type { ToastProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';

import type { BackendErrorMessageEntity } from '~/apiClient';
import { NotificationDuration } from '~/types/notifications.type';

export type NotifyParams = {
    title: string;
    description?: string;
    icon?: string;
    duration?: NotificationDuration;
    clickHandler?: (...args: any[]) => void;
    actions?: ToastProps['actions'];
};

export type NotifyBackendErrorParams = Omit<NotifyParams, 'description' | 'title'> & {
    error: BackendErrorMessageEntity;
    customMessages?: ErrorCodesToMessages;
};

export type UseNotificationReturn = {
    notifyGenericError: () => void;
    notifyError: (params: NotifyParams) => void;
    notifyBackendError: (params: NotifyBackendErrorParams) => void;
    notifySuccess: (params: NotifyParams) => void;
    notifyInfo: (params: NotifyParams) => void;
    notifyWarning: (params: NotifyParams) => void;
};

export const useNotification = (): UseNotificationReturn => {
    const { isSessionExpired } = useSessionExpiration();
    const { getErrorDescription } = useBackendErrors();
    const { tt } = useTypedI18n();
    const toast = useToast();

    const notifyError: (params: NotifyParams) => void = ({ title, description, duration, clickHandler, actions }) => {
        toast.add({
            title,
            description,
            color: 'error',
            icon: 'lucide:circle-x',
            duration: duration ?? NotificationDuration.Standard,
            onClick: clickHandler,
            actions,
        });
    };

    return {
        notifyGenericError: (): void => notifyError({ title: tt('notifications.error'), description: tt('notifications.genericError') }),

        notifyError,

        notifyBackendError: (params): void => {
            if (isSessionExpired(params.error.code)) {
                return;
            }

            notifyError({
                ...lodashOmit(params, 'customMessages'),
                title: tt('notifications.error'),
                description: getErrorDescription(params.error, params.customMessages),
            });
        },

        notifySuccess: ({ title, description, icon = 'lucide:party-popper', duration, clickHandler, actions }): void => {
            toast.add({
                title,
                description,
                color: 'success',
                icon,
                duration: duration ?? NotificationDuration.Standard,
                onClick: clickHandler,
                actions,
            });
        },

        notifyInfo: ({ title, description, icon = 'lucide:bell', duration, clickHandler, actions }): void => {
            toast.add({
                title,
                description,
                color: 'primary',
                icon,
                duration: duration ?? NotificationDuration.Standard,
                onClick: clickHandler,
                actions,
            });
        },

        notifyWarning: ({ title, description, icon = 'lucide:circle-x', duration, clickHandler, actions }): void => {
            toast.add({
                title,
                description,
                color: 'secondary',
                icon,
                duration: duration ?? NotificationDuration.Standard,
                onClick: clickHandler,
                actions,
            });
        },
    };
};
